import React from 'react';
import { Layout } from '../../components/layout/Layout';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Bot, BookOpen, Volume2, Target, Sparkles, Award, PlayCircle } from 'lucide-react';

export const TamilTutorPage: React.FC = () => {
  return (
    <Layout>
      <PageHeader
        title="🤖 Tamil Super Tutor! 🎓"
        subtitle="Your 24/7 Tamil learning buddy! 🌟"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Live Tutor Chat */}
        <Card className="bg-gradient-to-br from-indigo-400 to-purple-500 text-white shadow-2xl hover:scale-105 transition-all duration-300 border-4 border-indigo-200 group">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/30 rounded-2xl group-hover:bg-white/50 transition-all">
                <Bot className="w-12 h-12 text-yellow-300 animate-pulse" />
              </div>
              <CardTitle className="text-2xl font-black drop-shadow-lg">
                AI Tamil Guru 🤖
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-extrabold mb-4 drop-shadow-lg">24/7</p>
            <p className="text-yellow-100 font-semibold bg-white/20 px-4 py-2 rounded-xl inline-block mb-4">
              Always Online!
            </p>
            <ul className="space-y-2 text-lg mb-6">
              <li className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-yellow-300" />
                Ask any Tamil question
              </li>
              <li className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-yellow-300" />
                Grammar explained simply
              </li>
              <li className="flex items-center gap-3">
                <Volume2 className="w-5 h-5 text-yellow-300" />
                Perfect pronunciation
              </li>
            </ul>
            <button className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-black py-3 px-6 rounded-2xl shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2 text-lg">
              <PlayCircle className="w-6 h-6" />
              Start Chatting!
            </button>
          </CardContent>
        </Card>

        {/* Speaking Practice */}
        <Card className="bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-2xl hover:scale-105 transition-all duration-300 border-4 border-emerald-200 group">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/30 rounded-2xl group-hover:bg-white/50 transition-all">
                <Volume2 className="w-12 h-12 text-orange-300 animate-bounce" />
              </div>
              <CardTitle className="text-2xl font-black drop-shadow-lg">
                Speak Tamil 🎙️
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-5xl font-black mb-4 drop-shadow-lg">92%</p>
            <p className="text-orange-100 font-bold bg-white/20 px-6 py-3 rounded-2xl inline-block mb-6">
              Your Score
            </p>
            <div className="bg-black/30 p-6 rounded-3xl mb-6">
              <p className="text-2xl font-bold mb-4 text-orange-100">Repeat after me:</p>
              <div className="text-3xl mb-3">நான் படிக்கிறேன்</div>
              <p className="text-sm text-orange-200">"I am studying"</p>
            </div>
            <button className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-black py-4 px-8 rounded-2xl shadow-2xl hover:scale-105 transition-all text-lg">
              <Volume2 className="w-6 h-6" />
              Practice Speaking
            </button>
          </CardContent>
        </Card>

        {/* Daily Lessons */}
        <Card className="bg-gradient-to-br from-pink-400 to-rose-500 text-white shadow-2xl hover:scale-105 transition-all duration-300 border-4 border-pink-200 group">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/30 rounded-2xl group-hover:bg-white/50 transition-all">
                <BookOpen className="w-12 h-12 text-yellow-300 animate-spin" />
              </div>
              <CardTitle className="text-2xl font-black drop-shadow-lg">
                Daily Tamil Lessons 📚
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-extrabold mb-4 drop-shadow-lg">Day 7</p>
            <p className="text-yellow-100 font-bold bg-white/20 px-4 py-2 rounded-xl inline-block mb-6">
              Learning Streak!
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between p-4 bg-white/20 rounded-2xl">
                <span className="text-xl font-semibold">Today:</span>
                <span className="text-2xl font-black">உயர்வு (Height)</span>
              </div>
              <div className="flex items-center justify-between p-4 bg-white/20 rounded-2xl">
                <span className="text-xl font-semibold">Yesterday:</span>
                <span className="text-2xl font-bold">கீழ் (Down)</span>
              </div>
            </div>
            <button className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-black py-3 px-6 rounded-2xl shadow-xl hover:scale-105 transition-all flex items-center justify-center gap-2 text-lg">
              <Sparkles className="w-6 h-6" />
              New Lesson!
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Progress & Goals */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-orange-400 to-red-500 text-white shadow-2xl border-4 border-orange-200">
          <CardHeader>
            <CardTitle className="text-2xl font-black flex items-center gap-3">
              <Award className="w-10 h-10" />
              Your Progress 🚀
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="text-center">
                <p className="text-5xl font-black mb-2">85%</p>
                <p className="text-xl font-semibold text-yellow-100">Tamil Mastery</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white/20 rounded-2xl">
                  <Target className="w-12 h-12 mx-auto text-yellow-300 mb-2" />
                  <p className="font-bold text-xl">42</p>
                  <p className="text-sm">Words Learned</p>
                </div>
                <div className="text-center p-4 bg-white/20 rounded-2xl">
                  <Award className="w-12 h-12 mx-auto text-yellow-300 mb-2" />
                  <p className="text-2xl font-black">18</p>
                  <p className="text-sm">Stars Earned</p>
                </div>
                <div className="text-center p-4 bg-white/20 rounded-2xl">
                  <Sparkles className="w-12 h-12 mx-auto text-yellow-300 mb-2 animate-pulse" />
                  <p className="font-bold text-xl">Level 3</p>
                  <p className="text-sm">Super Student</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-400 to-cyan-500 text-white shadow-2xl border-4 border-blue-200">
          <CardHeader>
            <CardTitle className="text-2xl font-black flex items-center gap-3">
              <BookOpen className="w-10 h-10" />
              Quick Practice
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black p-8 rounded-3xl mb-6 shadow-2xl">
              <p className="text-3xl font-black mb-4">Translate this:</p>
              <div className="text-4xl font-bold mb-4 p-6 bg-white/50 rounded-2xl">
                Apple
              </div>
              <div className="text-3xl font-mono mb-6">____</div>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <button className="bg-white/50 hover:bg-white px-4 py-3 rounded-xl font-bold transition-all">செவ்வை</button>
                <button className="bg-white/50 hover:bg-white px-4 py-3 rounded-xl font-bold transition-all">ஆப்பிள்</button>
                <button className="bg-emerald-400 text-black px-4 py-3 rounded-xl font-bold shadow-lg transition-all">மாம்பழம்</button>
              </div>
            </div>
            <button className="w-full bg-white text-blue-600 font-black py-4 px-8 rounded-2xl text-xl shadow-2xl hover:scale-105 transition-all">
              Show Answer & Continue
            </button>
          </CardContent>
        </Card>
      </div>

      {/* Hero CTA */}
      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white p-12 rounded-3xl shadow-2xl border-4 border-white/30 text-center max-w-6xl mx-auto">
        <Bot className="w-28 h-28 mx-auto mb-8 text-yellow-300 animate-bounce" />
        <h2 className="text-5xl font-black mb-6 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent drop-shadow-2xl">
          Your Tamil Adventure Awaits! 🌟
        </h2>
        <p className="text-2xl mb-10 font-bold max-w-4xl mx-auto leading-relaxed">
          Chat with AI Guru, practice speaking, learn daily words, 
          and become Tamil Superstar! Every lesson = more stars! ⭐
        </p>
        <div className="flex flex-col lg:flex-row gap-6 justify-center">
          <button className="bg-white text-purple-600 font-black py-6 px-16 rounded-3xl text-2xl shadow-2xl hover:scale-105 transition-all flex items-center gap-4 mx-auto lg:mx-0">
            <Bot className="w-10 h-10" />
            Chat with Tutor
          </button>
          <button className="bg-gradient-to-r from-emerald-400 to-teal-500 text-white font-black py-6 px-16 rounded-3xl text-2xl shadow-2xl hover:scale-105 transition-all flex items-center gap-4 mx-auto lg:mx-0">
            <Volume2 className="w-10 h-10" />
            Start Speaking
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default TamilTutorPage;
